import { useState } from "react";
import { Sparkles, Loader2, Crown, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { aiBoostTitle, aiBoostDescription } from "@/lib/api";

/**
 * Reusable "✨ Booster avec l'IA" button.
 * - Pro users → triggers the IA endpoint and shows a suggestion picker.
 * - Non-Pro → opens an upsell modal pointing to /abonnements/pro.
 *
 * Usage:
 *   <AiBoosterButton kind="title" data={form} onApply={(v) => setForm({...form, title: v})}/>
 *   <AiBoosterButton kind="description" data={form} onApply={(v) => setForm({...form, description: v})}/>
 */
export default function AiBoosterButton({ kind, data, isPro, onApply }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showUpsell, setShowUpsell] = useState(false);

  const handleBoost = async () => {
    if (!isPro) { setShowUpsell(true); return; }
    setLoading(true);
    try {
      if (kind === "title") {
        const r = await aiBoostTitle({
          raw_title: data.title || "",
          category: data.category || "",
          city: data.city || "",
          description: data.description || "",
        });
        setSuggestions(r.suggestions || []);
      } else {
        const r = await aiBoostDescription({
          title: data.title || "",
          raw_description: data.description || "",
          category: data.category || "",
          city: data.city || "",
        });
        setSuggestions([r.description]);
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowUpsell(true);
      } else {
        toast.error(e?.response?.data?.detail || "Erreur de l'IA. Réessayez dans quelques secondes.");
      }
    } finally {
      setLoading(false);
    }
  };

  const apply = (s) => {
    onApply(s);
    setSuggestions([]);
    toast.success(kind === "title" ? "Titre appliqué" : "Description appliquée");
  };

  return (
    <div className="mt-2" data-testid={`ai-booster-${kind}`}>
      <button
        type="button"
        onClick={handleBoost}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-300 hover:shadow-md transition disabled:opacity-50"
        data-testid={`ai-boost-${kind}-btn`}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {loading ? "Optimisation…" : `Optimiser avec l'IA`}
        {!isPro && <Crown size={12} className="text-amber-600" />}
      </button>

      {suggestions.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`ai-suggestions-${kind}`}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => apply(s)}
              className="block w-full text-left p-3 rounded-2xl border-2 border-amber-200 bg-amber-50/50 hover:bg-amber-50 hover:border-amber-400 transition group"
              data-testid={`ai-suggestion-${kind}-${i}`}
            >
              <span className="text-sm text-[var(--ink)] whitespace-pre-wrap">{s}</span>
              <span className="block text-[10px] font-bold text-amber-700 mt-1 opacity-0 group-hover:opacity-100">
                <Check size={10} className="inline" /> Cliquer pour appliquer
              </span>
            </button>
          ))}
        </div>
      )}

      {showUpsell && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowUpsell(false)} data-testid="ai-upsell-modal">
          <div className="bg-white rounded-3xl p-6 max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <Crown className="mx-auto text-amber-500 mb-3" size={40} />
            <h3 className="text-xl font-bold text-center mb-2">Fonctionnalité Pro</h3>
            <p className="text-sm text-[var(--ink-soft)] text-center mb-5">
              L'IA Booster est réservée aux abonnés <b>Fodindin Pro</b>. 2 000 FCFA/mois, sans engagement.
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/abonnements/pro"
                className="inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold"
                data-testid="ai-upsell-cta"
              >
                <Crown size={14} /> Devenir Pro
              </Link>
              <button
                type="button"
                onClick={() => setShowUpsell(false)}
                className="py-2 text-sm text-[var(--ink-soft)]"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
